import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const LS_UNLOCKED = 'ea_unlocked';
const LS_CODE = 'ea_code';
const LS_CAMPAIGN = 'ea_campaign';
const LS_INFLUENCER = 'ea_influencer';
const LS_SESSION_ID = 'ea_session_id';
const LS_UNLOCKS_V2 = 'ea_unlocks_v2';

/** @param {unknown} v */
function normalizeCampaign(v) {
    return String(v || '')
        .trim()
        .toLowerCase();
}

/** @returns {HTMLElement | null} */
function getPrimaryUnlockRoot() {
    const el = document.querySelector('[data-ea-unlock]');
    return el instanceof HTMLElement ? el : null;
}

/** @returns {{ campaign: string, collectionHandle: string, context: string }} */
function getPageUnlockScope() {
    const root = getPrimaryUnlockRoot();
    if (!root) return { campaign: '', collectionHandle: '', context: '' };
    return {
        campaign: root.dataset.eaCampaign || '',
        collectionHandle: root.dataset.eaCollection || '',
        context: root.dataset.eaContext || '',
    };
}

/** @returns {Record<string, { code?: string, campaign?: string, influencer?: string, unlockedAt?: number }>} */
function getUnlockMap() {
    try {
        const raw = localStorage.getItem(LS_UNLOCKS_V2) || '';
        const json = raw ? JSON.parse(raw) : null;
        return json && typeof json === 'object' ? json : {};
    } catch (_e) {
        return {};
    }
}

/** @param {Record<string, any>} map */
function setUnlockMap(map) {
    try {
        localStorage.setItem(LS_UNLOCKS_V2, JSON.stringify(map || {}));
    } catch (_e) {
        // ignore
    }
}

/**
 * @typedef {Object} UnlockState
 * @property {boolean} unlocked
 * @property {boolean} loyaltyUnlocked
 * @property {boolean} codeUnlocked
 * @property {string} code
 * @property {string} campaign
 * @property {string} influencer
 */

/** @param {string} name */
function getCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')}=([^;]*)`));
    return match && match[1] ? decodeURIComponent(match[1]) : null;
}

/** @param {string} name @param {string} value @param {number} [days] */
function setCookie(name, value, days = 30) {
    const maxAge = days * 24 * 60 * 60;
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax`;
}

function getOrCreateSessionId() {
    const existing = localStorage.getItem(LS_SESSION_ID);
    if (existing) return existing;
    const sid = (crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
    localStorage.setItem(LS_SESSION_ID, sid);
    return sid;
}

/** @returns {UnlockState} */
function getUnlockState(scopeCampaign = '') {
    const scopeKey = normalizeCampaign(scopeCampaign);
    const loyaltyUnlocked =
        document.body?.dataset?.eaLoyaltyVip === 'true' || (document.body?.dataset?.eaLoyaltyTier || '') === 'VIP';
    const codeUnlocked = localStorage.getItem(LS_UNLOCKED) === '1' || getCookie('ea_unlocked') === '1';

    // Campaign-scoped unlocks (V2):
    // If the page declares a campaign, only treat the user as unlocked if *that campaign* is unlocked.
    // This prevents a previous event code from carrying over to a different event.
    const unlockMap = getUnlockMap();
    const scoped = scopeKey ? unlockMap[scopeKey] : null;

    // Back-compat migration:
    // If user has the old global unlock + a stored campaign that matches this page's campaign,
    // write it into the v2 map so future loads are correctly scoped.
    const legacyCode = localStorage.getItem(LS_CODE) || getCookie('ea_code') || '';
    const legacyCampaign = localStorage.getItem(LS_CAMPAIGN) || getCookie('ea_campaign') || '';
    const legacyInfluencer = localStorage.getItem(LS_INFLUENCER) || '';
    const legacyKey = normalizeCampaign(legacyCampaign);
    if (scopeKey && !scoped && codeUnlocked && legacyKey && legacyKey === scopeKey) {
        unlockMap[scopeKey] = {
            code: legacyCode || '',
            campaign: legacyCampaign || scopeCampaign || '',
            influencer: legacyInfluencer || '',
            unlockedAt: Date.now(),
        };
        setUnlockMap(unlockMap);
    }

    let unlocked = loyaltyUnlocked || codeUnlocked;
    let code = legacyCode;
    let campaign = legacyCampaign;
    const influencer = localStorage.getItem(LS_INFLUENCER) || '';

    if (scopeKey) {
        const v2 = getUnlockMap()[scopeKey];
        const v2Unlocked = Boolean(v2 && (v2.code || v2.influencer));
        unlocked = loyaltyUnlocked || v2Unlocked;
        code = (v2 && v2.code) || '';
        campaign = (v2 && v2.campaign) || scopeCampaign || '';
        // Note: keep "codeUnlocked" as the legacy/global indicator; scoped unlock uses v2Unlocked.
        // This prevents breaking other logic that expects codeUnlocked to exist.
    }

    return { unlocked, loyaltyUnlocked, codeUnlocked, code, campaign, influencer };
}

/** @param {{ code: string, campaign?: string, influencer?: string }} param0 */
function setUnlockState({ code, campaign, influencer }) {
    localStorage.setItem(LS_UNLOCKED, '1');
    localStorage.setItem(LS_CODE, code);
    setCookie('ea_unlocked', '1');
    setCookie('ea_code', code);

    if (campaign) {
        localStorage.setItem(LS_CAMPAIGN, campaign);
        setCookie('ea_campaign', campaign);
    }

    if (influencer) {
        localStorage.setItem(LS_INFLUENCER, influencer);
    }

    // V2: also persist a campaign-scoped unlock so future events don't inherit a previous unlock.
    const key = normalizeCampaign(campaign);
    if (key) {
        const map = getUnlockMap();
        map[key] = {
            code,
            campaign,
            influencer: influencer || '',
            unlockedAt: Date.now(),
        };
        setUnlockMap(map);
    }
}

function setBodyUnlockClasses() {
    const scope = getPageUnlockScope();
    const { unlocked } = getUnlockState(scope.campaign);
    document.body.classList.toggle('ea-unlocked', unlocked);
    document.body.classList.toggle('ea-locked', !unlocked);
}

function syncEarlyAccessLineItemProperties() {
    const state = getUnlockState();
    const wrappers = document.querySelectorAll('[data-ea-required="1"]');
    wrappers.forEach((wrapper) => {
        if (!(wrapper instanceof HTMLElement)) return;
        const forms = wrapper.querySelectorAll('form[data-type="add-to-cart-form"]');
        forms.forEach((form) => {
            if (!(form instanceof HTMLFormElement)) return;
            const codeEl = form.querySelector('input[data-ea-line-prop="code"]');
            const campaignEl = form.querySelector('input[data-ea-line-prop="campaign"]');
            if (codeEl instanceof HTMLInputElement) {
                // If unlocked via VIP/loyalty, store a meaningful value for ops.
                codeEl.value = state.code || (state.loyaltyUnlocked ? 'VIP' : '');
            }
            if (campaignEl instanceof HTMLInputElement) {
                campaignEl.value = state.campaign || '';
            }
        });
    });
}

function getCartSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
        if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return Array.from(ids);
}

async function removeEarlyAccessCartItemsIfLocked() {
    const state = getUnlockState();
    if (state.unlocked) return;
    if (typeof Theme === 'undefined' || !Theme?.routes?.cart_change_url) return;

    /** @type {Array<{ line: number }>} */
    const rows = Array.from(document.querySelectorAll('[data-ea-cart-item="1"][data-ea-cart-line]'))
        .map((el) => {
            if (!(el instanceof HTMLElement)) return null;
            const line = parseInt(el.dataset.eaCartLine || '', 10);
            return Number.isFinite(line) && line > 0 ? { line } : null;
        })
        .filter((v) => v !== null);

    if (rows.length === 0) return;

    // Remove from bottom-up so line numbers shift less.
    const lines = Array.from(new Set(rows.map((r) => r.line))).sort((a, b) => b - a);
    const sections = getCartSectionIds();
    const primarySectionId = sections[0] || '';

    for (const line of lines) {
        const body = JSON.stringify({
            line,
            quantity: 0,
            sections: sections.join(','),
            sections_url: window.location.pathname,
        });

        const res = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
        const text = await res.text();
        let json = null;
        try {
            json = JSON.parse(text);
        } catch (_e) {
            // ignore
        }
        if (!json?.sections || !primarySectionId) continue;

        // Dispatch a cart update so cart drawer/page re-renders immediately.
        let itemCount = 0;
        try {
            const doc = new DOMParser().parseFromString(json.sections[primarySectionId], 'text/html');
            const newCount = doc.querySelector('[ref="cartItemCount"]')?.textContent;
            itemCount = newCount ? parseInt(newCount, 10) : 0;
        } catch (_e) {
            // ignore
        }

        document.dispatchEvent(
            new CartUpdateEvent({}, primarySectionId, {
                itemCount,
                source: 'early-access',
                sections: json.sections,
            })
        );
    }
}

function preventEarlyAccessAddToCartIfLocked() {
    document.addEventListener(
        'submit',
        (e) => {
            const scope = getPageUnlockScope();
            const state = getUnlockState(scope.campaign);
            if (state.unlocked) return;
            const form = e.target;
            if (!(form instanceof HTMLFormElement)) return;

            // Only gate product add-to-cart forms inside EA required wrappers.
            if (form.dataset.type !== 'add-to-cart-form') return;
            const wrapper = form.closest('[data-ea-required="1"]');
            if (!wrapper) return;

            e.preventDefault();
            e.stopPropagation();

            // Nudge user toward the unlock banner if present.
            const unlock = document.querySelector('[data-ea-unlock]');
            unlock?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        },
        true
    );
}

/** @param {{ code: string, campaign?: string, influencer?: string }} param0 */
async function updateCartAttributes({ code, campaign, influencer }) {
    if (typeof Theme === 'undefined' || !Theme?.routes?.cart_update_url) return;

    /**
     * Public cart attributes are shown on Shopify checkout. Campaign / influencer must use
     * private attribute names (suffix `__`) per Cart API; also clear legacy public keys.
     * @see https://shopify.dev/docs/api/ajax/reference/cart#private-cart-attributes
     */
    /** @type {Record<string, string>} */
    const attrs = {
        early_access_code: code || '',
        early_access_campaign: '',
        early_access_influencer: '',
    };
    if (campaign) attrs.early_access_campaign__ = campaign;
    if (influencer) attrs.early_access_influencer__ = influencer;

    const body = JSON.stringify({ attributes: attrs });
    await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
}

/** @param {HTMLElement} root @param {string} code */
async function callProxyUnlock(root, code) {
    // IMPORTANT:
    // Some Shopify setups respond to POST /apps/early-access with a 301 redirect to the proxy URL.
    // Browsers often drop POST bodies on redirects, which causes "Missing code" backend outcomes.
    //
    // Use GET + query params so Shopify forwards them in the signed querystring reliably.
    const params = new URLSearchParams();
    params.set('code', code);
    params.set('campaign', root.dataset.eaCampaign || '');
    params.set('collectionHandle', root.dataset.eaCollection || '');
    params.set('shop', root.dataset.eaShop || Shopify?.shop || '');
    params.set('countryCode', root.dataset.eaCountry || '');
    params.set('sessionId', getOrCreateSessionId());
    // Avoid putting email/pageUrl in the querystring (PII/very long URLs).

    // Shopify App Proxy config:
    //   prefix: apps
    //   subpath: early-access
    //
    // Note: some stores only have the proxy registered for the base path (/apps/early-access),
    // and subpaths (e.g. /apps/early-access/unlock) can 404 at the Shopify edge.
    // We keep the unlock data in the querystring so it survives any redirect behavior.
    const url = `/apps/early-access?${params.toString()}`;
    const res = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
    });

    let json = null;
    try {
        json = await res.json();
    } catch (_e) {
        // ignore
    }

    if (!res.ok || !json) {
        const msg = json?.error || 'Unable to validate code. Please try again.';
        throw new Error(msg);
    }

    if (json.ok !== true) {
        const msg = json?.error || 'Invalid code.';
        throw new Error(msg);
    }

    return json;
}

/** @param {{ code: string, campaign?: string, collectionHandle?: string }} param0 */
function fireKlaviyoUnlockEvent({ code, campaign, collectionHandle }) {
    const learnq = window._learnq;
    if (!Array.isArray(learnq)) return;
    learnq.push([
        'track',
        'Early Access Unlocked',
        {
            code,
            campaign: campaign || '',
            collection_handle: collectionHandle || '',
            page_url: window.location.href,
        },
    ]);
}

/** @param {HTMLElement} root */
function mountUnlockForm(root) {
    const form = root.querySelector('[data-ea-unlock-form]');
    const msg = root.querySelector('[data-ea-message]');
    if (!(form instanceof HTMLFormElement) || !(msg instanceof HTMLElement)) return;
    const details = root.querySelector('.ea-unlock__details');
    const panel = root.querySelector('.ea-unlock__panel');
    const inner = panel instanceof HTMLElement ? panel.querySelector('.ea-unlock__panel-inner') : null;

    const syncPanelHeight = () => {
        if (!(panel instanceof HTMLElement)) return;
        // If closed, collapse immediately.
        if (!(details instanceof HTMLDetailsElement) || !details.open) {
            panel.style.setProperty('--ea-panel-max-height', '0px');
            return;
        }
        // If open, measure inner content height and animate to it.
        const h = (inner instanceof HTMLElement ? inner.scrollHeight : panel.scrollHeight) || 0;
        // Add a small buffer to avoid clipping from borders/sub-pixel rounding on different browsers.
        const bufferPx = 16;
        panel.style.setProperty('--ea-panel-max-height', `${h + bufferPx}px`);
    };

    if (details instanceof HTMLDetailsElement) {
        details.addEventListener('toggle', () => {
            // Wait a frame so layout reflects the new open state before measuring.
            requestAnimationFrame(syncPanelHeight);
        });
    }

    // Initialize height on mount.
    requestAnimationFrame(syncPanelHeight);

    // Keep the animation height accurate as content wraps/changes (mobile/desktop) or fonts load.
    const rafSync = () => requestAnimationFrame(syncPanelHeight);
    let ro = null;
    if (typeof ResizeObserver !== 'undefined' && inner instanceof HTMLElement) {
        ro = new ResizeObserver(() => rafSync());
        ro.observe(inner);
    }

    const onResize = () => rafSync();
    window.addEventListener('resize', onResize, { passive: true });

    form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const fd = new FormData(form);
        const rawCode = String(fd.get('code') || '').trim();
        const code = rawCode.toUpperCase();

        msg.hidden = true;
        msg.textContent = '';
        msg.removeAttribute('data-ea-status');

        if (!code) return;

        form.querySelector('button[type="submit"]')?.setAttribute('aria-busy', 'true');

        try {
            const resp = await callProxyUnlock(root, code);
            const campaign = resp.campaign || root.dataset.eaCampaign || '';
            const influencer = resp.influencer || '';

            setUnlockState({ code, campaign, influencer });
            syncEarlyAccessLineItemProperties();

            await updateCartAttributes({ code, campaign, influencer });

            fireKlaviyoUnlockEvent({
                code,
                campaign,
                collectionHandle: root.dataset.eaCollection || '',
            });

            msg.dataset.eaStatus = 'success';
            msg.textContent = 'Unlocked. Happy shopping.';
            msg.hidden = false;

            // Give the user a moment to see the success state before the banner collapses.
            window.setTimeout(() => {
                setBodyUnlockClasses();
            }, 450);
        } catch (err) {
            if (details instanceof HTMLDetailsElement) details.open = true;
            requestAnimationFrame(syncPanelHeight);
            msg.dataset.eaStatus = 'error';
            msg.textContent = err instanceof Error ? err.message : 'Invalid code.';
            msg.hidden = false;
        } finally {
            form.querySelector('button[type="submit"]')?.removeAttribute('aria-busy');
        }
    });

    // Best-effort cleanup if the banner is removed/re-rendered.
    const mo = new MutationObserver(() => {
        if (!document.body.contains(root)) {
            window.removeEventListener('resize', onResize);
            ro?.disconnect?.();
            mo.disconnect();
        }
    });
    mo.observe(document.body, { childList: true, subtree: true });
}

function init() {
    if (!document.body) return;

    // Set initial lock/unlock classes ASAP.
    setBodyUnlockClasses();
    preventEarlyAccessAddToCartIfLocked();
    syncEarlyAccessLineItemProperties();
    removeEarlyAccessCartItemsIfLocked().catch(() => { });

    // Keep cart attributes in sync (best-effort) if already unlocked.
    const state = getUnlockState();
    if (state.unlocked && state.code) {
        updateCartAttributes({ code: state.code, campaign: state.campaign, influencer: state.influencer }).catch(() => { });
    }

    // If forms are re-rendered (variant changes), re-apply EA properties.
    document.addEventListener(ThemeEvents.variantUpdate, () => syncEarlyAccessLineItemProperties());

    const roots = document.querySelectorAll('[data-ea-unlock]');
    roots.forEach((root) => {
        if (!(root instanceof HTMLElement)) return;
        mountUnlockForm(root);
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
    init();
}


